import React, { useRef, useState } from "react";
import { Paperclip, ExternalLink, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg";
const MAX_BYTES = 20 * 1024 * 1024;
const ALLOWED_MIME = new Set([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
]);

export default function DocumentCell({ task, onUpload, onRemove }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  const doc = task.document;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_MIME.has(file.type)) {
      toast.error("Only PDF, PNG, JPG, JPEG are allowed");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("File exceeds 20 MB limit");
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      await onUpload(task, file, setProgress);
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  if (busy) {
    return (
      <div
        className="inline-flex items-center gap-2 text-xs text-[#64748B]"
        data-testid={`task-upload-progress-${task.id}`}
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Uploading {progress}%</span>
      </div>
    );
  }

  if (doc) {
    return (
      <div className="inline-flex items-center gap-2">
        <a
          href={api.documentUrl(task.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-[#0F172A] hover:underline underline-offset-2"
          data-testid={TID.taskView(task.id)}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          View
        </a>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="text-xs text-[#64748B] hover:text-[#0F172A]"
          data-testid={`task-replace-${task.id}`}
          title="Replace document"
        >
          Replace
        </button>
        <button
          type="button"
          onClick={() => onRemove(task)}
          className="text-[#64748B] hover:text-[#DC2626]"
          data-testid={TID.taskRemoveDoc(task.id)}
          title="Remove document"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={handleFile}
        />
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-1 text-xs font-medium text-[#64748B] hover:text-[#0F172A]"
        data-testid={TID.taskUpload(task.id)}
      >
        <Paperclip className="h-3.5 w-3.5" />
        Upload
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={handleFile}
      />
    </>
  );
}
